/**
 * Centralized date formatting utilities with country-based locale support
 * Uses Intl.DateTimeFormat for locale-aware date formatting
 */

import { getLocaleForDate } from "./countryToLocale";

export type DateFormatOptions = {
  country?: string | null;
  timeZone?: string;
  includeTime?: boolean;
  includeSeconds?: boolean;
  format?: "short" | "long" | "numeric" | "2-digit";
};

/**
 * Format a date string with country-based locale
 * @param iso - ISO date string
 * @param options - Formatting options including country
 * @returns Formatted date string
 */
export function formatDate(
  iso: string | Date | undefined | null,
  options: DateFormatOptions = {},
): string {
  if (!iso) {
    return "N/A";
  }

  try {
    const date = typeof iso === "string" ? new Date(iso) : iso;
    if (isNaN(date.getTime())) {
      return typeof iso === "string" ? iso : "Invalid Date";
    }

    const locale = getLocaleForDate(options.country);
    const timeZone = options.timeZone || undefined;

    const formatOptions: Intl.DateTimeFormatOptions = {
      timeZone,
    };

    if (options.format === "short") {
      formatOptions.month = "short";
      formatOptions.day = "numeric";
      if (options.includeTime) {
        formatOptions.hour = "2-digit";
        formatOptions.minute = "2-digit";
      }
    } else if (options.format === "long") {
      formatOptions.year = "numeric";
      formatOptions.month = "long";
      formatOptions.day = "numeric";
      if (options.includeTime) {
        formatOptions.hour = "2-digit";
        formatOptions.minute = "2-digit";
        if (options.includeSeconds) {
          formatOptions.second = "2-digit";
        }
      }
    } else {
      // Default numeric format
      formatOptions.year = "numeric";
      formatOptions.month = "2-digit";
      formatOptions.day = "2-digit";
      if (options.includeTime) {
        formatOptions.hour = "2-digit";
        formatOptions.minute = "2-digit";
        if (options.includeSeconds) {
          formatOptions.second = "2-digit";
        }
        formatOptions.hour12 = false;
      }
    }

    const formatter = new Intl.DateTimeFormat(locale, formatOptions);
    return formatter.format(date);
  } catch (error) {
    console.error("Date formatting error:", error);
    return typeof iso === "string" ? iso : "Invalid Date";
  }
}

/**
 * Format date only (no time) with country-based locale
 * @param iso - ISO date string
 * @param country - Optional country name for locale
 * @returns Formatted date string (e.g., "12/31/2023" or "31/12/2023")
 */
export function formatDateOnly(
  iso: string | Date | undefined | null,
  country?: string | null,
): string {
  return formatDate(iso, {
    country,
    includeTime: false,
    format: "numeric",
  });
}

/**
 * Format date and time with country-based locale
 * @param iso - ISO date string
 * @param country - Optional country name for locale
 * @param includeSeconds - Whether to include seconds
 * @returns Formatted date and time string
 */
export function formatDateTime(
  iso: string | Date | undefined | null,
  country?: string | null,
  includeSeconds: boolean = false,
): string {
  return formatDate(iso, {
    country,
    includeTime: true,
    includeSeconds,
    format: "numeric",
  });
}

/**
 * Format short date (month name + day, optionally with time) with country-based locale
 * @param iso - ISO date string
 * @param country - Optional country name for locale
 * @param includeTime - Whether to include time
 * @returns Short formatted date (e.g., "Dec 31" or "Dec 31, 14:30")
 */
export function formatShortDate(
  iso: string | Date | undefined | null,
  country?: string | null,
  includeTime: boolean = true,
): string {
  return formatDate(iso, {
    country,
    includeTime,
    format: "short",
  });
}

/**
 * Format date with timezone support (for Asia/Jerusalem)
 * @param iso - ISO date string
 * @param country - Optional country name for locale
 * @param timeZone - Timezone (default: "Asia/Jerusalem")
 * @returns Formatted date string in specified timezone
 */
export function formatDateWithTimezone(
  iso: string | Date | undefined | null,
  country?: string | null,
  timeZone: string = "Asia/Jerusalem",
): string {
  return formatDate(iso, {
    country,
    timeZone,
    includeTime: true,
    includeSeconds: true,
    format: "numeric",
  });
}
